import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {clsx} from 'clsx';
import React from 'react';

import {legendTemplateRemoveCrosshairValues} from '../../util/legend';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {markToIconNameMap} from '../LineStylePicker';
import {type RenderLine} from '../vis/chartSeries';
import {ColorPaletteOption, getTextColor} from './../../util/colors';
import {
  type PlotFontSize,
  plotFontSizeToPx,
} from './../../util/plotHelpers/plotFontSize';
import {type GraphLegendFeature} from './graphRenderProps';

export type LinePlotLegendViewProps = {
  disallowSelect: boolean;
  fontSize: PlotFontSize;
  hiddenLines: RenderLine[];
  isMultiMetric: boolean;
  isVertical: boolean;
  legend: GraphLegendFeature;
  lines: RenderLine[];
};

/**
 * The line plot legend: one row per line with its mark, color, and name. Run
 * names link to the run unless links are disabled or the line is grouped.
 * Reads only its props.
 */
export function LinePlotLegendView({
  disallowSelect,
  fontSize,
  hiddenLines,
  isMultiMetric,
  isVertical,
  legend,
  lines,
}: LinePlotLegendViewProps) {
  const fSize = plotFontSizeToPx.legend[fontSize];
  const colorPalette = legend.colorPalette ?? ColorPaletteOption.DEFAULT;

  const wrapperClasses = clsx(
    `scrollbar-hide flex flex-shrink-0 flex-wrap gap-x-8 gap-y-2 leading-none`,
    {
      // tailwind requires the classes to be fully built for static analysis
      // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
      'text-[10px]': fSize === 10,
      'text-[12px]': fSize === 12,
      'text-[14px]': fSize === 14,
      'flex-column w-full items-center justify-center': isVertical,
      'flex-row justify-start': !isVertical,
      'select-none': disallowSelect,
    }
  );

  const hiddenLineIds = React.useMemo(
    () => new Set(hiddenLines.map(l => l.colorKey)),
    [hiddenLines]
  );

  return (
    <div className={wrapperClasses} data-test="line-plot-legend">
      {lines.map(line => {
        const hidden = hiddenLineIds.has(line.colorKey);
        const color = !hidden ? line.color : globals.gray500;

        // Full, untruncated legend text (run name + any extra legend fields),
        // with the crosshair value template (e.g. `[[ ${x}: ${y} ]]`) removed.
        const fullText = legendTemplateRemoveCrosshairValues(line.title ?? '');

        const runLink = legend.resolveRunLink?.({legendHref: line.legendHref});

        return (
          <div
            className={`flex items-center max-w-full min-w-0 ${
              isVertical ? '' : 'basis-full'
            }`}
            key={line.seriesId}>
            <span className="line-plot-color flex items-center" style={{color}}>
              <LegacyWBIcon name={markToIconNameMap[line.mark ?? 'solid']} />
            </span>
            <div
              data-test="line-plot-legend-run-name"
              className="min-w-0 flex-auto overflow-hidden text-wb-text-secondary"
              onMouseOver={() => {
                if (!legend.isSingleRun) {
                  legend.onHighlight?.(
                    line.highlightId ?? undefined,
                    isMultiMetric ? line.metricName : undefined
                  );
                }
              }}
              onMouseOut={() => {
                if (!legend.isSingleRun) {
                  legend.onHighlight?.(undefined, undefined);
                }
              }}
              style={{
                color:
                  colorPalette === ColorPaletteOption.DEFAULT &&
                  legend.colorRunNames
                    ? getTextColor(color)
                    : undefined,
              }}>
              <TruncateTextTooltip
                className="text-left"
                fullText={fullText}
                truncationType={TruncationType.Middle}
                linkPath={runLink}
                noLink={runLink == null}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
